// Client for /api/embed-visitors — server-side lead tracking for the embed
// onboarding page. Persists who visited, their role, and every prompt they
// clicked "Build it" on, BEFORE any OptiDev user/workspace exists, so
// abandoners can be re-engaged (email / welcome-back modal) and their prompt
// is restored on the next visit from any device.
//
// Auth: the OptiSigns parent Firebase token stashed by the bridge — same
// pattern as syncEmbedUserWithBackend. All calls are best-effort: tracking
// must never break the onboarding flow.

import { useEmbedStore } from '@/stores/embed-store';

const API_URL = import.meta.env.VITE_API_URL;

export interface EmbedPromptAttempt {
  text: string;
  at: string;
  event: 'build_clicked' | 'trial_declined';
}

export interface EmbedVisitorRecord {
  /** Last prompt they clicked "Build it" on. */
  draft: string;
  promptHistory: EmbedPromptAttempt[];
  status: 'visited' | 'build_clicked' | 'trial_declined' | 'converted';
  visitCount: number;
  updatedAt: string;
}

export type EmbedVisitorEvent = 'visit' | 'build_clicked' | 'trial_declined';

function parentToken(): string | null {
  return useEmbedStore.getState().parentToken;
}

/** The caller's own visitor row (null when new / unauthenticated / API down). */
export async function fetchEmbedVisitor(): Promise<EmbedVisitorRecord | null> {
  const token = parentToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_URL}/api/embed-visitors/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.visitor ?? null;
  } catch {
    return null;
  }
}

const MAX_TRACK_ATTEMPTS = 3;

const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * POST a tracking event, retrying transient failures (network error / 5xx).
 * The old fire-and-forget `void fetch().catch()` silently dropped ~a third of
 * events — a single transient failure or a browser dropping the request meant
 * permanent data loss. Retries fix that; the server-side write is idempotent
 * (atomic upsert keyed by scio_uid), so a retry can never double-count.
 *
 * `keepalive` lets the FIRST attempt still complete if the tab is closing
 * (e.g. right after declining the trial); later retries won't run once the
 * page is gone, but the next visit re-sends and the upsert reconciles.
 * 4xx is not retried — a bad request won't succeed on retry.
 */
async function postTrack(url: string, token: string, body: string): Promise<void> {
  for (let attempt = 1; attempt <= MAX_TRACK_ATTEMPTS; attempt++) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body,
        keepalive: true,
      });
      if (res.ok) return;
      // Client errors won't be fixed by retrying; give up.
      if (res.status >= 400 && res.status < 500) return;
      // 5xx — fall through to retry.
    } catch {
      // Network error — fall through to retry.
    }
    if (attempt < MAX_TRACK_ATTEMPTS) await delay(400 * attempt);
  }
}

/**
 * Record a funnel event (best-effort, retried). Scio context
 * (team/role/industry) rides along from the embed store so the lead row is
 * targetable. Never throws — tracking must not surface errors to the visitor.
 */
export function trackEmbedVisit(payload: {
  event: EmbedVisitorEvent;
  draft?: string;
  fileCount?: number;
}): void {
  const token = parentToken();
  if (!token) return;
  const state = useEmbedStore.getState();
  const body = {
    ...payload,
    scioTeamId: state.scioTeamId ?? undefined,
    scioTeamName: state.scioTeamName ?? undefined,
    scioRole: state.scioRole ?? undefined,
    isAdminOrAbove: !state.needsAdminAccess,
    industry: state.industry?.primary ?? undefined,
  };
  void postTrack(`${API_URL}/api/embed-visitors/track`, token, JSON.stringify(body));
}
